
import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";

interface Job {
  id: string;
  title: string;
  description?: string | null;
  contact_id?: string | null;
}

interface JobsSelectionProps {
  companyId: string;
  contactId?: string;
  selectedJobs: string[];
  onJobsChange: (selectedJobs: string[]) => void;
  disabled?: boolean;
}

export const JobsSelection = ({
  companyId,
  contactId,
  selectedJobs,
  onJobsChange,
  disabled = false
}: JobsSelectionProps) => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    fetchJobs();
  }, [companyId, contactId]);
  
  const fetchJobs = async () => {
    if (!companyId) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('jobs')
        .select('id, title, description, contact_id')
        .eq('company_id', companyId);
      
      if (error) throw error;
      
      setJobs(data || []);
      
      // If editing an existing contact, fetch linked jobs
      if (contactId) {
        const { data: linkedJobs, error: linkedError } = await supabase
          .from('jobs')
          .select('id')
          .eq('contact_id', contactId);
        
        if (!linkedError && linkedJobs) {
          onJobsChange(linkedJobs.map(job => job.id));
        }
      }
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const toggleJobSelection = (jobId: string) => {
    if (disabled) return;
    
    const newSelectedJobs = selectedJobs.includes(jobId)
      ? selectedJobs.filter(id => id !== jobId)
      : [...selectedJobs, jobId];
    
    onJobsChange(newSelectedJobs);
  };
  
  return (
    <div>
      <Label className="mb-2 block">
        {contactId ? 'Linked Job Positions' : 'Link to Job Positions'}
      </Label>
      {loading ? (
        <div className="animate-pulse space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-8 bg-gray-200 rounded-sm"></div>
          ))}
        </div>
      ) : jobs.length > 0 ? (
        <div className="space-y-2 max-h-60 overflow-y-auto border rounded-sm p-2">
          {jobs.map(job => (
            <div key={job.id} className="flex items-start space-x-2">
              <Checkbox 
                id={`job-${job.id}`}
                checked={selectedJobs.includes(job.id)}
                onCheckedChange={() => toggleJobSelection(job.id)}
                disabled={disabled}
                className="mr-2 mt-0.5"
              />
              <Label 
                htmlFor={`job-${job.id}`}
                className={`font-normal cursor-pointer ${disabled ? 'opacity-70' : ''}`}
              >
                {job.title}
              </Label>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500">No job positions available</p>
      )}
    </div>
  );
};
